// Pure egress filter for every PostHog event (PostHog client spec §9; before_send).
// Closed allow-lists from analytics-events.js: unknown events are dropped, unknown
// properties deleted, our values coerced, and $exception rebuilt so no message text,
// source context or URL query leaves the device. It may throw on structurally
// unexpected input -- analytics.js safeScrub turns that into a dropped event. It never
// reports and never deep-clones ($snapshot_data passes by reference).
import {
  ALLOWED_EVENTS, COMMON_PROPS, EVENT_PROPS, SCREENS, ONBOARDING_STEPS, BOOKS, LEAGUES, OUTCOMES, REFUSAL_REASONS,
  IMPORT_STAGES, IMPORT_REASONS, TAG_RE, UUID_RE, EXCEPTION, SYNC_STAGES, SYNC_EXIT_REASONS, AUTH_REASONS,
  SKIP_REASONS, BRIDGE_STEPS,
  SYNC_ERROR_CLASSES, SYNC_CONTENT_TYPES, SYNC_STOPPED_AT, SYNC_TRAIL_MAX, SYNC_KEY_RE, SYNC_KEYS_MAX, DETECTED_SPORTS } from './analytics-events';

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isInt = (v) => Number.isInteger(v);
const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const ENUMS = {
  screen_viewed: { screen: SCREENS },
  onboarding_step_viewed: { step: ONBOARDING_STEPS },
  import_completed: { book: BOOKS, league: LEAGUES },
  import_failed: { stage: IMPORT_STAGES, reason_code: IMPORT_REASONS, detected_sport: DETECTED_SPORTS },
  slipsync_run_completed: {
    book: BOOKS, outcome: OUTCOMES, stage: SYNC_STAGES, exit_reason: SYNC_EXIT_REASONS, bridge_last: BRIDGE_STEPS,
    last_auth_reason: AUTH_REASONS, last_skip: SKIP_REASONS,
    error_class: SYNC_ERROR_CLASSES, book_reason: AUTH_REASONS, content_type: SYNC_CONTENT_TYPES, stopped_at: SYNC_STOPPED_AT,
  },
  slipsync_refused: { book: BOOKS, reason: REFUSAL_REASONS },
};
const TAG_EVENTS = new Set(['client_warning', '$exception']);
const COUNT_PROPS = [['injects', 1000], ['auth_errors', 1000], ['unbound', 100000], ['slip_count', 1000],
  // #1659: the server's sync result counts.
  ['imported_count', 1000], ['skipped_count', 1000], ['failed_count', 1000], ['untracked_count', 5000]];
// #1587: on slipsync_run_completed only, these codes are absent on some exits (no bridge result, no auth reply);
// null there means "none", not 'other'. Other events keep null -> 'other'.
const NULLABLE_CODES = new Set(['stage', 'exit_reason', 'bridge_last', 'last_auth_reason', 'last_skip']);
const TAG_KEYS = ['fn', 'stage', 'boundary'];

function coerce(name, props) {
  const enums = ENUMS[name] || {};
  for (const [key, allowed] of Object.entries(enums)) {
    const nullable = name === 'slipsync_run_completed' && NULLABLE_CODES.has(key);
    if (has(props, key) && !(props[key] === null && nullable) && !allowed.includes(props[key])) props[key] = 'other';
  }
  if (has(props, 'leg_count')) {
    const n = props.leg_count;
    props.leg_count = isInt(n) && n >= 0 && n <= 100 ? n : null;
  }
  // #1587: bounded counts on slipsync_run_completed; anything else becomes null. A null coded field stays null
  // (no value is not 'other').
  for (const [key, max] of COUNT_PROPS) {
    if (has(props, key) && props[key] !== null) {
      const n = props[key];
      props[key] = isInt(n) && n >= 0 && n <= max ? n : null;
    }
  }
  if (has(props, 'http_status') && props.http_status !== null) {
    const n = props.http_status;
    props.http_status = isInt(n) && n >= 100 && n <= 599 ? n : null;
  }
  // #1711: a JSON-RPC error code is an int32; anything else becomes null.
  if (has(props, 'rpc_code') && props.rpc_code !== null) {
    const n = props.rpc_code;
    props.rpc_code = isInt(n) && n >= -2147483648 && n <= 2147483647 ? n : null;
  }
  if (has(props, 'attempt_id') && !(typeof props.attempt_id === 'string' && UUID_RE.test(props.attempt_id))) {
    delete props.attempt_id;
  }
  // SlipSync trail ('>'-joined closed tokens) and the book's top-level key names (','-joined), 2026-10-18.
  if (has(props, 'trail')) {
    if (typeof props.trail === 'string' && props.trail) {
      props.trail = props.trail.split('>').slice(0, SYNC_TRAIL_MAX).map((t) => (BRIDGE_STEPS.includes(t) ? t : 'other')).join('>');
    } else delete props.trail;
  }
  if (has(props, 'keys')) {
    const keys = typeof props.keys === 'string' ? props.keys.split(',').filter((k) => SYNC_KEY_RE.test(k)).slice(0, SYNC_KEYS_MAX) : [];
    if (keys.length) props.keys = keys.join(','); else delete props.keys;
  }
  if (TAG_EVENTS.has(name)) {
    for (const key of TAG_KEYS) {
      if (has(props, key) && !(typeof props[key] === 'string' && TAG_RE.test(props[key]))) delete props[key];
    }
  }
}

function rebuildMechanism(m) {
  const src = isObject(m) ? m : {};
  const out = { type: typeof src.type === 'string' && src.type.length <= 32 ? src.type : 'generic' };
  if (typeof src.handled === 'boolean') out.handled = src.handled;
  if (typeof src.synthetic === 'boolean') out.synthetic = src.synthetic;
  if (isInt(src.exception_id)) out.exception_id = src.exception_id;
  if (isInt(src.parent_id)) out.parent_id = src.parent_id;
  if (typeof src.source === 'string' && src.source.length <= 64 && EXCEPTION.SOURCE_RE.test(src.source)) out.source = src.source;
  return out;
}

function rebuildFrame(f) {
  const out = { platform: 'web:javascript' };
  if (typeof f.filename === 'string') out.filename = f.filename.split(/[?#]/)[0];
  out.function = typeof f.function === 'string' && EXCEPTION.FUNCTION_RE.test(f.function) ? f.function : '?';
  out.in_app = f.in_app === true;
  if (isInt(f.lineno)) out.lineno = f.lineno;
  if (isInt(f.colno)) out.colno = f.colno;
  if (typeof f.chunk_id === 'string' && f.chunk_id.length <= 128) out.chunk_id = f.chunk_id;
  return out;
}

function rebuildEntry(e) {
  const src = isObject(e) ? e : {};
  const out = {
    type: typeof src.type === 'string' && EXCEPTION.TYPE_RE.test(src.type) ? src.type : 'Error',
    value: '', // C7: exception message text never leaves the device
    mechanism: rebuildMechanism(src.mechanism),
  };
  if (isObject(src.stacktrace) && Array.isArray(src.stacktrace.frames)) {
    const frames = src.stacktrace.frames
      .filter((f) => isObject(f) && f.platform === 'web:javascript')
      .slice(0, EXCEPTION.MAX_FRAMES) // same bound as the SDK's own STACKTRACE_FRAME_LIMIT
      .map(rebuildFrame);
    out.stacktrace = { type: 'raw', frames };
  }
  return out;
}

function rebuildException(props) {
  if (!Array.isArray(props.$exception_list)) throw new TypeError('scrub: $exception_list is not an array');
  props.$exception_list = props.$exception_list.slice(0, EXCEPTION.MAX_ENTRIES).map(rebuildEntry);
  if (!EXCEPTION.LEVELS.includes(props.$exception_level)) props.$exception_level = 'error';
  if (has(props, '$release_id')
    && !(typeof props.$release_id === 'string' && EXCEPTION.RELEASE_RE.test(props.$release_id))) {
    delete props.$release_id;
  }
}

export function scrub(event, { appVersion }) {
  // 1
  if (event == null) return null;
  // 2
  const name = event.event;
  if (!ALLOWED_EVENTS.has(name)) return null;
  if (event.properties != null && !isObject(event.properties)) throw new TypeError('scrub: properties is not an object');

  // 3 (shallow copies only)
  const out = { ...event };
  const props = { ...(event.properties || {}) };
  delete out.$set;
  delete out.$set_once;
  delete props.$set;
  delete props.$set_once;

  // 4
  const allowed = new Set([...COMMON_PROPS, ...EVENT_PROPS[name]]);
  for (const key of Object.keys(props)) {
    if (!allowed.has(key)) delete props[key];
  }

  // 5
  props.app_version = appVersion;
  props.platform = 'ios';
  coerce(name, props);

  // 6
  if (name === '$exception') rebuildException(props);

  // 7
  out.properties = props;
  return out;
}
